import { useMemo, useState } from 'react';

interface SubProductData {
  id: string;
  name: string;
  count: number;
}

interface ProductData {
  id: string;
  name: string;
  count: number;
  subProducts: SubProductData[];
  /** Present only on the synthetic "All Other" rollup row — the real product ids it represents */
  aggregatedIds?: string[];
}

interface StackedBarChartProps {
  title: string;
  data: ProductData[];
  total: number;
  /** Navigate to the List view pre-filtered to the clicked product(s) */
  onProductClick?: (productIds: string[]) => void;
}

// Generate color shades for sub-products within a product
function generateSubProductColors(baseColor: string, count: number): string[] {
  if (count === 0) return [];
  if (count === 1) return [baseColor];

  // Parse hex color to RGB
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Generate shades from lighter to base color
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    const factor = 0.3 + (0.7 * (i / Math.max(count - 1, 1)));
    const newR = Math.round(255 - (255 - r) * factor);
    const newG = Math.round(255 - (255 - g) * factor);
    const newB = Math.round(255 - (255 - b) * factor);
    colors.push(`rgb(${newR}, ${newG}, ${newB})`);
  }
  return colors;
}

// Base colors for products
const PRODUCT_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
];

// Distinct, muted color for the synthetic "All Other" rollup row — never reused by real products
const OTHER_COLOR = '#9ca3af'; // gray-400

const MAX_ROWS = 20;

interface TooltipData {
  product: ProductData;
  x: number;
  y: number;
}

export function StackedBarChart({ title, data, total, onProductClick }: StackedBarChartProps) {
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);

  // Sort by count descending; beyond the top 20, roll everything else into a single
  // "All Other" row (summed count, no sub-product breakdown) so the chart never silently
  // drops data — and the total always reconciles with what's drawn.
  const sortedData = useMemo(() => {
    const nonZero = [...data].filter((p) => p.count > 0).sort((a, b) => b.count - a.count);
    const top = nonZero.slice(0, MAX_ROWS);
    const rest = nonZero.slice(MAX_ROWS);
    if (rest.length === 0) return top;

    const otherRow: ProductData = {
      id: '__other__',
      name: `All Other (${rest.length} product${rest.length === 1 ? '' : 's'})`,
      count: rest.reduce((sum, p) => sum + p.count, 0),
      subProducts: [],
      aggregatedIds: rest.map((p) => p.id),
    };
    return [...top, otherRow];
  }, [data]);

  // Find max count for scaling
  const maxCount = useMemo(() => {
    return Math.max(...sortedData.map(p => p.count), 1);
  }, [sortedData]);

  // Calculate bar width percentage
  const getBarWidth = (count: number) => {
    return (count / maxCount) * 100;
  };

  const handleMouseEnter = (product: ProductData, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      product,
      x: event.clientX,
      y: rect.top,
    });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          {title}
        </h3>
        <span className="text-2xl font-bold text-gray-900">{total}</span>
      </div>

      {sortedData.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
          No data to display
        </div>
      ) : (
        <div className="flex-1 space-y-2 relative">
          {sortedData.map((product, productIndex) => {
            const isOther = !!product.aggregatedIds;
            const baseColor = isOther ? OTHER_COLOR : PRODUCT_COLORS[productIndex % PRODUCT_COLORS.length];
            const subProductColors = generateSubProductColors(
              baseColor,
              product.subProducts.length
            );
            const barWidth = getBarWidth(product.count);
            const clickIds = product.aggregatedIds ?? [product.id];

            return (
              <div
                key={product.id}
                className={`flex items-center gap-3 rounded px-1 -mx-1 ${onProductClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                onMouseEnter={(e) => handleMouseEnter(product, e)}
                onMouseLeave={handleMouseLeave}
                onClick={() => onProductClick?.(clickIds)}
                title={onProductClick ? `View ${product.name} in List` : undefined}
              >
                {/* Product Name */}
                <div className="w-48 flex-shrink-0">
                  <span className={`text-xs font-medium truncate block ${isOther ? 'text-gray-500 italic' : 'text-gray-700'}`}>
                    {product.name}
                  </span>
                </div>

                {/* Stacked Bar */}
                <div className="flex-1 h-6 flex items-center">
                  <div
                    className="h-full flex rounded overflow-hidden transition-all duration-300"
                    style={{ width: `${barWidth}%`, minWidth: product.count > 0 ? '20px' : '0' }}
                  >
                    {isOther ? (
                      <div
                        className="h-full w-full flex items-center justify-center text-xs font-medium transition-all duration-300"
                        style={{ backgroundColor: OTHER_COLOR, color: 'white' }}
                      >
                        {product.count}
                      </div>
                    ) : (
                      product.subProducts.map((sp, spIndex) => {
                        const segmentWidth = product.count > 0
                          ? (sp.count / product.count) * 100
                          : 0;
                        const showLabel = segmentWidth > 15 && sp.count > 0;

                        return (
                          <div
                            key={sp.id}
                            className="h-full flex items-center justify-center text-xs font-medium transition-all duration-300"
                            style={{
                              width: `${segmentWidth}%`,
                              backgroundColor: subProductColors[spIndex] || baseColor,
                              color: spIndex >= subProductColors.length / 2 ? 'white' : '#374151',
                            }}
                            title={`${sp.name}: ${sp.count}`}
                          >
                            {showLabel && (
                              <span className="px-1 truncate" style={{
                                color: spIndex >= Math.floor(subProductColors.length / 2) ? 'white' : '#374151'
                              }}>
                                {sp.count}
                              </span>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Total Count */}
                <div className="w-8 flex-shrink-0 text-right">
                  <span className="text-xs font-semibold text-gray-700">
                    {product.count}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Tooltip */}
          {tooltip && (
            <div
              className="fixed z-50 bg-gray-900 text-white text-xs rounded px-2 py-1.5 shadow-lg pointer-events-none max-w-xs"
              style={{
                left: tooltip.x + 10,
                top: tooltip.y - 10,
                transform: 'translateY(-100%)',
              }}
            >
              <div className="font-medium mb-1">
                {tooltip.product.name}: {tooltip.product.count}
              </div>
              {tooltip.product.subProducts.map((sp) => (
                <div key={sp.id} className="text-gray-300">
                  {sp.name}: {sp.count}
                </div>
              ))}
              {tooltip.product.aggregatedIds && (
                <div className="text-gray-300">Click to view all in List</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
